import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogContent, DialogTitle,
  IconButton, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { wealthService, apiErrorMessage } from '../../utils/api';

const MANUAL_FALLBACK_MS = 2500;
// Detailed CAS: MFC answers "still generating" for a while after the QR is issued.
// Resend the same QR with backoff (seconds), up to ~5 minutes in total.
const GENERATING_RETRY_S = [5, 5, 10, 10, 15, 20, 30, 30, 30, 30, 30, 30, 30, 30];
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const isTrustedOrigin = (origin, expected) => {
  if (!origin) return false;
  if (expected && origin === expected) return true;
  try {
    const host = new URL(origin).hostname;
    return host === 'mfcentral.com' || host.endsWith('.mfcentral.com');
  } catch {
    return false;
  }
};

// MFC sends the QR as 'mfc-cas-download' { base64 } for Summary CAS; accept other shapes too
// (field name, a data-URL string, a JSON-string message) so a Detailed CAS isn't missed.
const QR_FIELDS = ['base64', 'qrCode', 'qr', 'qrImage', 'image', 'dataUrl', 'file'];

const parseMessage = (raw) => {
  if (typeof raw === 'string') {
    try { return JSON.parse(raw); } catch { return { type: raw }; }
  }
  return raw || {};
};

const extractQr = (data) => {
  if (typeof data === 'string') return data.startsWith('data:image') ? data : null;
  if (!data || typeof data !== 'object') return null;
  const hit = QR_FIELDS.map((k) => data[k]).find((v) => typeof v === 'string' && v.length > 20);
  return hit || null;
};

const readAsBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

/**
 * Hosts the MF Central OTP flow in an iframe. MFC posts:
 *   'mfc-cas-download' { base64 }                 → QR captured automatically
 *   'mfc-cas-complete' { status, type, code, message }
 * If no QR arrives after a successful completion, the user uploads it manually.
 */
function MfcCasModal({ session, onClose, onComplete }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [phase, setPhase] = useState('verifying'); // verifying | fetching | manual | error
  const [error, setError] = useState('');
  const handledRef = useRef(false);
  const casTypeRef = useRef(null);
  const timerRef = useRef(null);
  const [preparing, setPreparing] = useState(false);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const submitQr = useCallback(async (base64) => {
    if (handledRef.current || !base64) return;
    handledRef.current = true;
    clearTimeout(timerRef.current);
    setPhase('fetching');
    setError('');
    setPreparing(false);
    try {
      let result = await wealthService.submitMfcQr(session.request_id, base64, casTypeRef.current);
      for (const delay of GENERATING_RETRY_S) {
        if (result?.status !== 'GENERATING' || !mountedRef.current) break;
        setPreparing(true);
        await sleep(delay * 1000);
        if (!mountedRef.current) return;
        result = await wealthService.submitMfcQr(session.request_id, base64, casTypeRef.current);
      }
      if (!mountedRef.current) return;
      if (result?.status === 'GENERATING') {
        handledRef.current = false;
        setPreparing(false);
        setError('MF Central is still preparing your statement. Please upload the QR again in a few minutes.');
        setPhase('manual');
        return;
      }
      onComplete(result);
    } catch (err) {
      if (!mountedRef.current) return;
      handledRef.current = false;
      setPreparing(false);
      setError(apiErrorMessage(err, 'Could not read your statement. Please upload the QR again.'));
      setPhase('manual');
    }
  }, [session, onComplete]);

  useEffect(() => {
    const onMessage = (event) => {
      if (!isTrustedOrigin(event.origin, session.expected_origin)) return;
      const msg = parseMessage(event.data);
      const { type } = msg;
      const rawData = msg.data ?? msg.payload ?? {};
      const data = rawData && typeof rawData === 'object' ? rawData : {};
      const qr = extractQr(rawData) || extractQr(msg);
      // Diagnostics: message type and field names only, never the QR itself.
      console.info('[MFC] message', type, typeof rawData === 'object' ? Object.keys(data) : typeof rawData,
        qr ? `(QR found, ${qr.length} chars)` : '(no QR)');

      if (type === 'mfc-cas-download' || (qr && type !== 'mfc-cas-complete')) {
        if (qr) submitQr(qr);
        else console.warn('[MFC] download message without a recognisable QR');
      } else if (type === 'mfc-cas-complete') {
        if (qr) submitQr(qr); // some flows may attach the QR to the completion message
        casTypeRef.current = data.type || null;
        wealthService.sendMfcEvent(session.request_id, {
          status: data.status, type: data.type, code: data.code, message: data.message,
        }).catch(() => {});

        if (data.status === 'success') {
          if (!handledRef.current) {
            timerRef.current = setTimeout(() => {
              if (!handledRef.current) setPhase('manual');
            }, MANUAL_FALLBACK_MS);
          }
        } else {
          setError(data.message || 'MF Central could not complete the request.');
          setPhase('error');
        }
      }
    };
    window.addEventListener('message', onMessage);
    return () => {
      window.removeEventListener('message', onMessage);
      clearTimeout(timerRef.current);
    };
  }, [session, submitQr]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      submitQr(await readAsBase64(file));
    } catch {
      setError('Could not read that file. Please choose the QR image.');
    }
  };

  const openPopup = () => window.open(session.mfc_url, 'mfc-cas', 'width=520,height=760');

  return (
    <Dialog open onClose={phase === 'fetching' && !preparing ? undefined : onClose} fullScreen={fullScreen} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pr: 6 }}>
        MF Central verification
        <IconButton
          aria-label="Close"
          onClick={onClose}
          disabled={phase === 'fetching' && !preparing}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
        {phase === 'fetching' && (
          <Box sx={{ p: 4, textAlign: 'center' }} role="status" aria-live="polite">
            <CircularProgress sx={{ mb: 2 }} />
            <Typography>
              {preparing
                ? 'MF Central is preparing your detailed statement. This can take a minute or two…'
                : 'Fetching your mutual fund holdings…'}
            </Typography>
          </Box>
        )}

        {phase === 'error' && (
          <Box sx={{ p: 3 }}>
            <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
            <Button variant="contained" onClick={onClose} sx={{ bgcolor: '#000', '&:hover': { bgcolor: '#222' } }}>
              Close and try again
            </Button>
          </Box>
        )}

        {phase === 'manual' && (
          <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              Verified. Download the QR code below, then upload it here to finish.
            </Typography>
            <Button
              component="label"
              variant="contained"
              startIcon={<UploadFileIcon />}
              sx={{ bgcolor: '#000', '&:hover': { bgcolor: '#222' } }}
            >
              Upload QR code
              <input hidden type="file" accept="image/png,image/jpeg" onChange={handleFile} />
            </Button>
          </Box>
        )}

        {/* Kept mounted while fetching so a failed upload doesn't restart the OTP flow */}
        {phase !== 'error' && (
          <Box sx={{ display: phase === 'fetching' ? 'none' : 'flex', flexDirection: 'column', flex: 1 }}>
            <Box
              component="iframe"
              title="MF Central verification"
              src={session.mfc_url}
              sx={{ border: 0, width: '100%', flex: 1, minHeight: fullScreen ? 0 : 640 }}
            />
            <Box sx={{ p: 1.5, textAlign: 'center' }}>
              <Button size="small" onClick={openPopup}>Page not loading? Open in a new window</Button>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default MfcCasModal;
